import { useCallback, useEffect, useMemo, useState } from "react";
import { Column } from "../../../DataTable/DataTable";
import {
  ClassificationType,
  HierarchyRow,
  MasterOption,
  MasterRow,
  ProductCatalogService,
} from "../../../../services/api/productCatalog.service";
import { FilterSelect, MasterPanel, StatusPill } from "./MasterPanel";

type InnerTab = "primary" | "subcategory" | "group" | "mapping";

const INNER_TABS: { id: InnerTab; label: string }[] = [
  { id: "primary", label: "Primary Category" },
  { id: "subcategory", label: "Sub Category" },
  { id: "group", label: "Product Group" },
  { id: "mapping", label: "Category Mapping" },
];

const ACTIVE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const toBool = (value: string): boolean | undefined =>
  value === "" ? undefined : value === "true";

/**
 * Masters and mapping for the category tree.
 *
 * The three masters are per-classification (a MEDICINE primary category and a
 * NON_MEDICINE one are different rows even with the same name), so the
 * classification toggle at the top drives every inner tab, including which
 * options the mapping form offers.
 */
export function CategoryMappingTab() {
  const [classification, setClassification] =
    useState<ClassificationType>("MEDICINE");
  const [innerTab, setInnerTab] = useState<InnerTab>("primary");
  const [activeFilter, setActiveFilter] = useState("");

  // Options for the mapping form, reloaded whenever the classification changes.
  const [primaryOptions, setPrimaryOptions] = useState<MasterOption[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<MasterOption[]>(
    []
  );
  const [groupOptions, setGroupOptions] = useState<MasterOption[]>([]);

  // Mapping-tab filters, which also narrow the hierarchy listing.
  const [filterPrimary, setFilterPrimary] = useState("");
  const [filterSubcategory, setFilterSubcategory] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      const [primary, subcategory, group] = await Promise.all([
        ProductCatalogService.getPrimaryCategoryOptions({
          classificationType: classification,
          isActive: true,
        }),
        ProductCatalogService.getSubcategoryOptions({
          classificationType: classification,
          isActive: true,
        }),
        ProductCatalogService.getGroupOptions({
          classificationType: classification,
          isActive: true,
        }),
      ]);
      if (cancelled) return;
      setPrimaryOptions(primary?.success ? primary.data || [] : []);
      setSubcategoryOptions(subcategory?.success ? subcategory.data || [] : []);
      setGroupOptions(group?.success ? group.data || [] : []);
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
  }, [classification]);

  // Reset the mapping filters when the tree changes - their ids belong to the
  // old classification and would silently return nothing.
  useEffect(() => {
    setFilterPrimary("");
    setFilterSubcategory("");
  }, [classification]);

  const asSelectOptions = (options: MasterOption[]) =>
    options.map((option) => ({ value: option.id, label: option.name }));

  const masterColumns: Column<MasterRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Name",
        width: "w-[320px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">{row.name}</span>
        ),
      },
      {
        key: "classificationType",
        title: "Classification",
        width: "w-[160px]",
        render: (row) => (
          <span className="text-sm text-gray-600">
            {row.classificationType === "NON_MEDICINE"
              ? "Non Medicine"
              : "Medicine"}
          </span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const primaryColumns: Column<MasterRow>[] = useMemo(
    () => [
      {
        key: "name",
        title: "Name",
        width: "w-[280px]",
        render: (row) => (
          <div className="flex items-center gap-2">
            {row.categoryIcon && (
              <img
                src={row.categoryIcon}
                alt={row.name}
                className="w-7 h-7 rounded object-cover border border-gray-200"
              />
            )}
            <span className="text-sm font-medium text-gray-800">
              {row.name}
            </span>
          </div>
        ),
      },
      {
        key: "sortOrder",
        title: "Sort Order",
        width: "w-[110px]",
        render: (row) => (
          <span className="text-sm text-gray-600">{row.sortOrder ?? 0}</span>
        ),
      },
      {
        key: "isVisible",
        title: "Visible",
        width: "w-[110px]",
        render: (row) => (
          <StatusPill value={row.isVisible} trueLabel="Yes" falseLabel="No" />
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const hierarchyColumns: Column<HierarchyRow>[] = useMemo(
    () => [
      {
        key: "primaryCategoryName",
        title: "Primary Category",
        width: "w-[220px]",
        render: (row) => (
          <span className="text-sm font-medium text-gray-800">
            {row.primaryCategoryName || row.primaryCategoryId}
          </span>
        ),
      },
      {
        key: "subcategoryName",
        title: "Sub Category",
        width: "w-[220px]",
        render: (row) => (
          <span className="text-sm text-gray-700">
            {row.subcategoryName || row.subcategoryId}
          </span>
        ),
      },
      {
        key: "groupName",
        title: "Product Group",
        width: "w-[220px]",
        render: (row) => (
          <span className="text-sm text-gray-700">
            {row.groupName || row.groupId}
          </span>
        ),
      },
      {
        key: "isActive",
        title: "Status",
        width: "w-[120px]",
        render: (row) => <StatusPill value={row.isActive} />,
      },
    ],
    []
  );

  const fetchPrimary = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getPrimaryCategories({
        page,
        pageSize,
        search,
        classificationType: classification,
        isActive: toBool(activeFilter),
      }),
    [classification, activeFilter]
  );

  const fetchSubcategories = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getSubcategories({
        page,
        pageSize,
        search,
        classificationType: classification,
        isActive: toBool(activeFilter),
      }),
    [classification, activeFilter]
  );

  const fetchGroups = useCallback(
    ({ page, pageSize, search }: { page: number; pageSize: number; search: string }) =>
      ProductCatalogService.getGroups({
        page,
        pageSize,
        search,
        classificationType: classification,
        isActive: toBool(activeFilter),
      }),
    [classification, activeFilter]
  );

  const fetchHierarchies = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) =>
      ProductCatalogService.getHierarchies({
        page,
        pageSize,
        classificationType: classification,
        primaryCategoryId: filterPrimary || undefined,
        subcategoryId: filterSubcategory || undefined,
        isActive: toBool(activeFilter),
      }),
    [classification, filterPrimary, filterSubcategory, activeFilter]
  );

  const activeFilterControl = (
    <FilterSelect
      label="Status"
      value={activeFilter}
      onChange={setActiveFilter}
      options={ACTIVE_FILTER_OPTIONS}
      width="w-32"
    />
  );

  return (
    <div className="space-y-5">
      {/* Classification toggle - the masters below belong to one tree at a time. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
          {(["MEDICINE", "NON_MEDICINE"] as ClassificationType[]).map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => setClassification(type)}
              className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                classification === type
                  ? "bg-emerald-50 text-emerald-700"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {type === "MEDICINE" ? "Medicine" : "Non Medicine"}
            </button>
          ))}
        </div>

        <nav className="flex flex-wrap gap-2">
          {INNER_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setInnerTab(tab.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                innerTab === tab.id
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        {innerTab === "primary" && (
          <MasterPanel<MasterRow>
            title="Primary Category"
            description="Top level of the category tree. Drives which domains a product may use."
            addLabel="Add Primary Category"
            columns={primaryColumns}
            searchPlaceholder="Search primary categories..."
            filters={activeFilterControl}
            refreshToken={`${classification}|${activeFilter}`}
            fetchRows={fetchPrimary}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                createOnly: true,
                placeholder: "e.g. Pain Relief",
              },
              {
                name: "categoryIcon",
                label: "Category Icon URL",
                type: "text",
                placeholder: "https://...",
                defaultValue: "",
              },
              {
                name: "sortOrder",
                label: "Sort Order",
                type: "number",
                placeholder: "Leave blank to append at the end",
                defaultValue: "",
              },
              { name: "isVisible", label: "Visible", type: "boolean" },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({
              categoryIcon: row.categoryIcon ?? "",
              sortOrder: row.sortOrder ?? "",
              isVisible: row.isVisible ?? true,
              isActive: row.isActive,
            })}
            onCreate={(values) =>
              ProductCatalogService.createPrimaryCategory({
                name: values.name,
                classificationType: classification,
                categoryIcon: values.categoryIcon || null,
                sortOrder: values.sortOrder,
                isVisible: values.isVisible,
                isActive: values.isActive,
              })
            }
            onUpdate={(row, values) =>
              ProductCatalogService.updatePrimaryCategory(String(row.id), {
                categoryIcon: values.categoryIcon || null,
                sortOrder: values.sortOrder,
                isVisible: values.isVisible,
                isActive: values.isActive,
              })
            }
          />
        )}

        {innerTab === "subcategory" && (
          <MasterPanel<MasterRow>
            title="Sub Category"
            description="Second level of the tree. Reusable across primary categories through the mapping."
            addLabel="Add Sub Category"
            columns={masterColumns}
            searchPlaceholder="Search sub categories..."
            filters={activeFilterControl}
            refreshToken={`${classification}|${activeFilter}`}
            fetchRows={fetchSubcategories}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                createOnly: true,
                placeholder: "e.g. Analgesics",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ isActive: row.isActive })}
            onCreate={(values) =>
              ProductCatalogService.createSubcategory({
                name: values.name,
                classificationType: classification,
                isActive: values.isActive,
              })
            }
            onUpdate={(row, values) =>
              ProductCatalogService.updateSubcategory(String(row.id), {
                isActive: values.isActive,
              })
            }
          />
        )}

        {innerTab === "group" && (
          <MasterPanel<MasterRow>
            title="Product Group"
            description="Third level of the tree - the narrowest grouping a product is filed under."
            addLabel="Add Product Group"
            columns={masterColumns}
            searchPlaceholder="Search product groups..."
            filters={activeFilterControl}
            refreshToken={`${classification}|${activeFilter}`}
            fetchRows={fetchGroups}
            fields={[
              {
                name: "name",
                label: "Name",
                type: "text",
                required: true,
                createOnly: true,
                placeholder: "e.g. NSAIDs",
              },
              { name: "isActive", label: "Active", type: "boolean" },
            ]}
            rowToValues={(row) => ({ isActive: row.isActive })}
            onCreate={(values) =>
              ProductCatalogService.createGroup({
                name: values.name,
                classificationType: classification,
                isActive: values.isActive,
              })
            }
            onUpdate={(row, values) =>
              ProductCatalogService.updateGroup(String(row.id), {
                isActive: values.isActive,
              })
            }
          />
        )}

        {innerTab === "mapping" && (
          <MasterPanel<HierarchyRow>
            title="Category Mapping"
            description="The valid primary category + sub category + product group combinations. Products map to one of these rows."
            addLabel="Add Mapping"
            columns={hierarchyColumns}
            searchable={false}
            refreshToken={`${classification}|${activeFilter}|${filterPrimary}|${filterSubcategory}`}
            fetchRows={fetchHierarchies}
            filters={
              <>
                <FilterSelect
                  label="Primary Category"
                  value={filterPrimary}
                  onChange={(value) => {
                    setFilterPrimary(value);
                    setFilterSubcategory("");
                  }}
                  options={[
                    { value: "", label: "All" },
                    ...asSelectOptions(primaryOptions),
                  ]}
                />
                <FilterSelect
                  label="Sub Category"
                  value={filterSubcategory}
                  onChange={setFilterSubcategory}
                  options={[
                    { value: "", label: "All" },
                    ...asSelectOptions(subcategoryOptions),
                  ]}
                />
                {activeFilterControl}
              </>
            }
            fields={[
              {
                name: "primaryCategoryId",
                label: "Primary Category",
                type: "select",
                required: true,
                createOnly: true,
                options: asSelectOptions(primaryOptions),
              },
              {
                name: "subcategoryId",
                label: "Sub Category",
                type: "select",
                required: true,
                createOnly: true,
                options: asSelectOptions(subcategoryOptions),
              },
              {
                name: "groupId",
                label: "Product Group",
                type: "select",
                required: true,
                createOnly: true,
                options: asSelectOptions(groupOptions),
              },
              {
                name: "isActive",
                label: "Active",
                type: "boolean",
                // New mappings are active by default and the create endpoint
                // takes no flag, so this only appears when editing.
                editOnly: true,
                helpText:
                  "A mapping cannot be deactivated while products are still filed under it.",
              },
            ]}
            rowToValues={(row) => ({ isActive: row.isActive })}
            onCreate={(values) =>
              ProductCatalogService.createHierarchy({
                classificationType: classification,
                primaryCategoryId: values.primaryCategoryId,
                subcategoryId: values.subcategoryId,
                groupId: values.groupId,
              })
            }
            onUpdate={(row, values) =>
              ProductCatalogService.setHierarchyActive(
                String(row.id),
                Boolean(values.isActive)
              )
            }
          />
        )}
      </div>
    </div>
  );
}
